/**
 * @file RegisterBusinessPage.tsx
 * @brief `/register/business`: Register Business, the phone's
 *        RegisterBusiness + RegisterBusinessPassword + the two post-signup
 *        questions, on one route.
 *
 * @details
 * Company first (it is what this form is for), then the admin and their
 * contact details, then the admin's password. The server creates the
 * company and its admin account together; the admin then signs in and
 * lands on the company space.
 *
 * @references
 * - FRONTEND/src/screens/Auth/RegisterBusiness.tsx, RegisterBusinessPassword.tsx
 * - BACKEND/src/features/auth/routes.js (registerCompanySchema)
 *
 * @author DES
 * @date 01-10-2026
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Building2, Globe, Mail, Phone, User } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { FormFieldGroup } from "@/components/ui/FormDialog";
import { Input } from "@/components/ui/Input";
import { toast } from "@/shared/hooks/useToast";
import { checkCompanyName, checkEmailAvailable, registerCompany, acceptCurrentTerms } from "./registerApi";
import {
  describeRegisterError,
  phoneNumberSchema,
  useTakenCheck,
  type RegisteredState,
} from "./registerShared";
import {
  PasswordStep,
  RegisterHeader,
  RegisterSection,
  SignInLink,
  SignupQuestions,
  TitleField,
} from "./registerUi";

const schema = z
  .object({
    companyName: z.string().trim().min(1, "Company name is required"),
    businessDomain: z.string().trim().optional(),
    title: z.string().min(1, "Required"),
    firstName: z.string().trim().min(1, "Required"),
    lastName: z.string().trim().min(1, "Required"),
    email: z.string().trim().email("Enter a valid email"),
    confirmEmail: z.string().trim().email("Enter a valid email"),
    phoneNumber: phoneNumberSchema,
  })
  .refine((d) => d.email.toLowerCase() === d.confirmEmail.toLowerCase(), {
    message: "Emails don't match",
    path: ["confirmEmail"],
  });
type FormValues = z.infer<typeof schema>;

const COMPANY_TAKEN = "A company with this name already exists";

const emailCheck = async (email: string) => {
  const available = await checkEmailAvailable(email);
  return available === null ? null : { available };
};

export function RegisterBusinessPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<"details" | "password" | "questions">("details");
  const [details, setDetails] = useState<FormValues | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    control,
    handleSubmit,
    setError,
    clearErrors,
    getFieldState,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: "onTouched",
    defaultValues: {
      companyName: "",
      businessDomain: "",
      title: "",
      firstName: "",
      lastName: "",
      email: "",
      confirmEmail: "",
      phoneNumber: "",
    },
  });

  const values = useWatch({ control });
  const email = (values.email ?? "").trim();
  const companyName = (values.companyName ?? "").trim();
  const emailTaken = useTakenCheck(email, z.string().email().safeParse(email).success, emailCheck);
  const nameTaken = useTakenCheck(companyName, companyName.length >= 2, checkCompanyName);

  // A "taken" error belongs to the value it was found for: editing the
  // field drops it, and the live check flags the new value if it is taken too.
  useEffect(() => {
    if (getFieldState("email").error?.type === "taken") clearErrors("email");
  }, [email, getFieldState, clearErrors]);
  useEffect(() => {
    if (getFieldState("companyName").error?.type === "taken") clearErrors("companyName");
  }, [companyName, getFieldState, clearErrors]);
  useEffect(() => {
    if (emailTaken.takenValue) {
      setError("email", { type: "taken", message: "Email already registered" });
    }
  }, [emailTaken.takenValue, setError]);
  useEffect(() => {
    if (nameTaken.takenValue) {
      setError("companyName", { type: "taken", message: nameTaken.message ?? COMPANY_TAKEN });
    }
  }, [nameTaken.takenValue, nameTaken.message, setError]);

  const filled = (keys: (keyof FormValues)[]) =>
    keys.every((k) => String(values[k] ?? "").trim().length > 0 && !errors[k]);

  const onContinue = handleSubmit(async (v) => {
    // Ask once more on Continue (phone, 29-09-2026): an unanswered live
    // check must not let a taken name or email through.
    const [name, emailFree] = await Promise.all([
      checkCompanyName(v.companyName.trim()),
      checkEmailAvailable(v.email.trim()),
    ]);
    let blocked = false;
    if (nameTaken.takenValue || name?.available === false) {
      setError("companyName", {
        type: "taken",
        message: name?.message ?? nameTaken.message ?? COMPANY_TAKEN,
      });
      blocked = true;
    }
    if (emailTaken.takenValue || emailFree === false) {
      setError("email", { type: "taken", message: "Email already registered" });
      blocked = true;
    }
    if (blocked) return;
    setDetails(v);
    setServerError(null);
    setStep("password");
  });

  const goLogin = () => navigate("/login");

  const finish = () => {
    if (!details) return;
    toast({
      title: "Company Registered!",
      description:
        "Your business account has been created successfully. Please log in to access your dashboard.",
    });
    const state: RegisteredState = {
      registeredEmail: details.email.trim(),
      accountType: "business",
    };
    navigate("/login", { replace: true, state });
  };

  if (step === "questions" && token) {
    return <SignupQuestions token={token} onDone={finish} />;
  }

  if (step === "password" && details) {
    return (
      <PasswordStep
        title="Secure Business Account"
        subtitle="Set a strong password for your administrator account."
        onBack={() => setStep("details")}
        serverError={serverError}
        onCreate={async (password) => {
          setServerError(null);
          try {
            const domain = details.businessDomain?.trim();
            const res = await registerCompany({
              companyName: details.companyName.trim(),
              ...(domain ? { businessDomain: domain } : {}),
              title: details.title,
              firstName: details.firstName.trim(),
              lastName: details.lastName.trim(),
              email: details.email.trim(),
              phoneNumber: details.phoneNumber.trim(),
              password,
            });
            setToken(res.token);
            void acceptCurrentTerms(res.token);
            setStep("questions");
          } catch (err) {
            setServerError(describeRegisterError(err));
          }
        }}
      />
    );
  }

  return (
    <div>
      <RegisterHeader
        title="Register Business"
        subtitle={"Set up your company, then\ninvite your team."}
        onBack={goLogin}
      />

      {/* Two columns from `sm` (DES 01-10-2026): the form fits one screen
          without scrolling, the desktop app's window included. Reading order
          stays the phone's; Continue sits beside the last section. */}
      <form
        onSubmit={onContinue}
        className="grid gap-x-10 gap-y-7 sm:grid-cols-2 [@media(max-height:720px)]:gap-y-4"
        noValidate
      >
        <RegisterSection label="Company Information" done={filled(["companyName"])}>
          <FormFieldGroup
            label="Company Name"
            htmlFor="companyName"
            icon={<Building2 />}
            error={errors.companyName?.message}
          >
            <Input capitalizeFirst id="companyName" autoComplete="organization" {...register("companyName")} />
          </FormFieldGroup>
          <FormFieldGroup
            label="Business Domain (Optional)"
            htmlFor="businessDomain"
            icon={<Globe />}
            error={errors.businessDomain?.message}
          >
            <Input id="businessDomain" autoComplete="url" {...register("businessDomain")} />
          </FormFieldGroup>
        </RegisterSection>

        <RegisterSection
          label="Admin Information"
          done={filled(["title", "firstName", "lastName"])}
        >
          <Controller
            control={control}
            name="title"
            render={({ field }) => (
              <TitleField value={field.value} onChange={field.onChange} error={errors.title?.message} />
            )}
          />
          <FormFieldGroup label="First Name" htmlFor="firstName" icon={<User />} error={errors.firstName?.message}>
            <Input id="firstName" autoComplete="given-name" capitalizeFirst {...register("firstName")} />
          </FormFieldGroup>
          <FormFieldGroup label="Last Name" htmlFor="lastName" icon={<User />} error={errors.lastName?.message}>
            <Input id="lastName" autoComplete="family-name" capitalizeFirst {...register("lastName")} />
          </FormFieldGroup>
        </RegisterSection>

        <RegisterSection
          label="Contact Information"
          done={filled(["email", "confirmEmail", "phoneNumber"])}
        >
          <FormFieldGroup label="Email Address" htmlFor="email" icon={<Mail />} error={errors.email?.message}>
            <Input id="email" type="email" autoComplete="email" {...register("email")} />
          </FormFieldGroup>
          <FormFieldGroup
            label="Confirm Email"
            htmlFor="confirmEmail"
            icon={<Mail />}
            error={errors.confirmEmail?.message}
          >
            <Input id="confirmEmail" type="email" autoComplete="email" {...register("confirmEmail")} />
          </FormFieldGroup>
          <FormFieldGroup label="Phone Number" htmlFor="phoneNumber" icon={<Phone />} error={errors.phoneNumber?.message}>
            <Input id="phoneNumber" type="tel" autoComplete="tel" {...register("phoneNumber")} />
          </FormFieldGroup>
        </RegisterSection>

        <div className="sm:self-end">
          <Button
            type="submit"
            className="h-12 w-full bg-brand text-[15px] font-semibold text-brand-foreground hover:bg-brand/90"
            disabled={isSubmitting}
            size="lg"
          >
            Continue
          </Button>
          <SignInLink onClick={goLogin} />
        </div>
      </form>
    </div>
  );
}
